// RITA-1552 — Ein Block im Aktionsprotokoll der Karte „Vertragserstellung“ (Skizze Rücklauf vom 21.10.2026, Bilder A
// und B; F6, F7 von Julia „wie empfohlen“ freigegeben; E4, E5 vom 22.10.2026). Durch eine feine Linie abgetrennt:
// links der Titel „<Vertragsvorlage> · gedruckt am …“ und darunter der Fortschritt (✓ erledigt, ○ offen), rechts in
// derselben Höhe „Nächster Schritt: …“, „✓ Abgeschlossen am …“ (ohne Scan mit grauem „(ohne Scan)“) oder „Ersetzt durch
// Vertrag vom …“ – Letzteres nur hier im Kopf, nicht an der Druckzeile (E5). Darunter der Knopf „Aktionsprotokoll
// ausblenden ▴ / anzeigen ▾“ und, wenn offen, die Protokollzeilen des Blocks (von Aktionsprotokoll.tsx gerendert und
// als children übergeben – so importiert dieser Baustein nichts aus dem Aktionsprotokoll, kein Import-Zyklus).
// Ohne Vertragsdaten (`block` = null) ist es der Block „Ohne Vertrag aus dem PRM“ (E4): nur Titel und Knopf.
// Was im Block steht und welcher Status gilt, entscheidet services/vertragsbloecke.ts; hier wird nur angezeigt.
import React from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  fortschrittsChips,
  statusText,
  Vertragsblock as VertragsblockDaten,
  Vertragsstatus,
} from '../services/vertragsbloecke';
import {
  abgeschlossenText,
  OHNE_SCAN_ZUSATZ,
  OHNE_VERTRAG_BLOCK,
  PROTOKOLL_ANZEIGEN,
  PROTOKOLL_AUSBLENDEN,
} from '../services/vertragsdruckTexte';

interface Props {
  /** Vertrag mit Fortschritt und Status; null = Block „Ohne Vertrag aus dem PRM“ (nur Titel, ohne Chips und Status). */
  block: VertragsblockDaten | null;
  /** Älterer Vertrag bzw. „Ohne Vertrag“ neben Vertragsblöcken: Titel grau wie in Skizze B. */
  grau?: boolean;
  /** Protokoll des Blocks aufgeklappt. */
  offen: boolean;
  onUmschalten: () => void;
  /** Die Protokollzeilen des Blocks; nur sichtbar, solange er offen ist. */
  children?: React.ReactNode;
}

/** Rechts im Kopf: nächster Schritt (blau), abgeschlossen (grün, „(ohne Scan)“ grau) oder ersetzt (grauer Hinweis). */
function Status({ status }: { status: Vertragsstatus }) {
  if (status.art === 'ersetzt') {
    return (
      <Typography
        data-testid="vertragsblock-status"
        data-art={status.art}
        sx={{ fontSize: 13, color: 'text.secondary', pt: 0.5 }}
      >
        {statusText(status)}
      </Typography>
    );
  }
  const abgeschlossen = status.art === 'abgeschlossen';
  return (
    <Typography
      component="div"
      data-testid="vertragsblock-status"
      data-art={status.art}
      sx={(theme) => {
        const farbe = abgeschlossen ? theme.palette.primary : theme.palette.secondary;
        return {
          fontSize: 13,
          fontWeight: 700,
          borderRadius: 1,
          px: 1.25,
          py: 0.5,
          color: theme.palette.mode === 'light' ? farbe.dark : farbe.light,
          bgcolor: alpha(farbe.main, 0.12),
        };
      }}
    >
      {status.art === 'abgeschlossen' ? (
        <>
          {abgeschlossenText(status.datum, false)}
          {status.ohneScan && (
            <>
              {' '}
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                {OHNE_SCAN_ZUSATZ}
              </Box>
            </>
          )}
        </>
      ) : (
        statusText(status)
      )}
    </Typography>
  );
}

function Vertragsblock({ block, grau = false, offen, onUmschalten, children }: Props) {
  return (
    <Box
      data-testid="vertragsblock"
      data-block={block ? block.kopf.id : 'ohne-vertrag'}
      sx={{ borderTop: 1, borderColor: 'divider', mt: 2, pt: 1.5 }}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{ fontSize: 14, fontWeight: 700, color: grau ? 'text.secondary' : 'text.primary' }}
            data-testid="vertragsblock-titel"
          >
            {block ? block.titel : OHNE_VERTRAG_BLOCK}
          </Typography>
          {block && (
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
              {fortschrittsChips(block).map((chip) => (
                <Chip
                  key={chip.schritt}
                  size="small"
                  variant="soft"
                  color={chip.erreicht ? 'primary' : 'default'}
                  label={chip.text}
                  data-testid={`vertragsblock-chip-${chip.schritt}`}
                  data-erreicht={chip.erreicht}
                  sx={{ fontWeight: 600, ...(chip.erreicht ? {} : { color: 'text.secondary' }) }}
                />
              ))}
            </Box>
          )}
        </Box>
        {block && <Status status={block.status} />}
      </Box>
      <Button
        variant="text"
        size="small"
        onClick={onUmschalten}
        aria-expanded={offen}
        data-testid="vertragsblock-protokoll-umschalten"
        sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, minWidth: 0, px: 0.5, mt: 0.5 }}
      >
        {offen ? PROTOKOLL_AUSBLENDEN : PROTOKOLL_ANZEIGEN}
      </Button>
      {offen && <Box data-testid="vertragsblock-protokoll">{children}</Box>}
    </Box>
  );
}

export default Vertragsblock;
